import React, { useState, useEffect, useCallback } from "react";
import RoleSidebar from "./components/RoleSidebar";
import PermissionEditor from "./components/PermissionEditor";
import { useRoles } from "./hooks/useRoles";
import { roleDefaults } from "../../../models/role";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * Roles Page (Cargos e Permissões)
 * Layout Master-Detail moderno utilizando componentes padronizados.
 */
const Roles = () => {
    const { roles, loading, isSaving, actions } = useRoles();

    // Estado para o cargo ativamente selecionado no editor
    const [selectedRole, setSelectedRole] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // Selecionar o primeiro cargo automaticamente ao carregar
    useEffect(() => {
        if (!selectedRole && roles.length > 0 && !loading) {
            setSelectedRole(roles[0]);
        }
    }, [roles, loading, selectedRole]);

    /**
     * Selecionar um cargo da lista (Protegido por confirmação se houver alterações)
     */
    const handleSelectRole = useCallback(async (role) => {
        if (selectedRole?.id === role.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações não salvas. Deseja descartá-las e mudar de cargo?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedRole(role);
        setIsDirty(false);
    }, [selectedRole, isDirty]);

    /**
     * Preparar para criar um novo cargo (limpa o editor)
     */
    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar um novo cargo?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedRole(roleDefaults);
        setIsDirty(false);
    }, [isDirty]);

    /**
     * Salvar alterações (incluindo criação)
     */
    const handleSave = useCallback(async (data) => {
        const success = await actions.saveRole(data);
        if (success) {
            setSelectedRole(null);
            setIsDirty(false);
        }
    }, [actions]);

    /**
     * Deletar cargo
     */
    const handleDelete = useCallback(async (roleId) => {
        const success = await actions.deleteRole(roleId);
        if (success) {
            setSelectedRole(null);
        }
    }, [actions]);

    // PageLoader logic removido para carregamento nativo do sidebar/detalhe

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Cargos e Permissões" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="roles-container mt-3">
                <MasterDetailLayout
                master={
                    <RoleSidebar
                        roles={roles}
                        activeId={selectedRole?.id}
                        onSelect={handleSelectRole}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                    />
                }
                detail={
                    <PermissionEditor
                        role={selectedRole}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        isSaving={isSaving}
                        onChange={() => setIsDirty(true)}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default Roles;
